import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Download, ChevronRight, ArrowLeft, Loader2 } from 'lucide-react'
import { Button } from 'components/ui/button'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from 'components/ui/table'
import { Badge } from 'components/ui/badge'
import { initializeTemplateTasks } from 'slices/templateSlice'
import notify from 'components/ui/notify'

const PRIORITY_STYLES: Record<string, { text: string; bg: string; border: string }> = {
	'P1 - Urgent': { text: 'rgb(239, 68, 68)', bg: 'rgba(239, 68, 68, 0.1)', border: 'rgba(239, 68, 68, 0.2)' },
	'P2 - High': { text: 'rgb(249, 115, 22)', bg: 'rgba(249, 115, 22, 0.1)', border: 'rgba(249, 115, 22, 0.2)' },
	'P3 - Medium': { text: 'rgb(62, 124, 177)', bg: 'rgba(62, 124, 177, 0.1)', border: 'rgba(62, 124, 177, 0.2)' },
	'P4 - Low': { text: 'rgb(113, 113, 122)', bg: 'rgba(113, 113, 122, 0.1)', border: 'rgba(113, 113, 122, 0.2)' },
}

interface Template {
	_id: string
	templateName: string
	description?: string
	accessLevel?: string
	createdBy?: { name?: string }
}

interface TemplateTask {
	_id: string
	taskName: string
	description?: string
	seq: number
	priority?: string
	effort?: string
	taskDays?: number
}

interface TaskTemplatesSelectCNProps {
	taskTemplates: Template[]
	handleTamplateLoad: (templateId: string) => void
}

const TaskTemplatesSelectCN = ({ taskTemplates, handleTamplateLoad }: TaskTemplatesSelectCNProps) => {
	const dispatch = useDispatch()
	const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null)

	const {
		entities: taskEntities,
		ids: taskIds,
		isLoading: tasksLoading,
	} = useSelector((state: any) => state.templates.tasks)

	const templateTasks: TemplateTask[] = (taskIds || [])
		.map((id: string) => taskEntities[id])
		.filter(Boolean)
		.sort((a: TemplateTask, b: TemplateTask) => (a.seq || 0) - (b.seq || 0))

	const handleTemplateClick = (template: Template) => {
		setSelectedTemplate(template)
		dispatch(initializeTemplateTasks(template._id) as any)
	}

	const handleBack = () => {
		setSelectedTemplate(null)
	}

	const handleDownload = (template: Template) => {
		notify({
			text: `This will download the ${template.templateName} template to your Task list.`,
			buttons: ['Cancel', 'Ok!'],
		}).then(value => {
			if (value) {
				handleTamplateLoad(template._id)
				notify('Downloaded!', `The ${template.templateName} template has been downloaded to your Task list.`, 'success')
			}
		})
	}

	// ─── Task Preview View ──────────────────────────────────────────────────────

	if (selectedTemplate) {
		return (
			<div className="min-w-[30rem] max-w-[45rem] flex flex-col gap-3 p-3">
				{/* Header with back button */}
				<div className="flex items-center gap-3">
					<button
						type="button"
						onClick={handleBack}
						className="bg-transparent border-none p-1 rounded-md hover:bg-gray-100 cursor-pointer text-gray-500 hover:text-gray-700 transition-colors"
					>
						<ArrowLeft className="w-4 h-4" />
					</button>
					<div className="flex-1 min-w-0">
						<h3 className="text-sm font-semibold text-gray-900 m-0 truncate">
							{selectedTemplate.templateName}
						</h3>
						{selectedTemplate.description && (
							<p className="text-xs text-gray-500 m-0 mt-0.5 truncate">{selectedTemplate.description}</p>
						)}
					</div>
					<Button onClick={() => handleDownload(selectedTemplate)} className="shrink-0">
						<Download className="w-3.5 h-3.5 mr-1.5" />
						Download
					</Button>
				</div>

				{/* Task list */}
				<div className="border border-gray-200 rounded-lg overflow-hidden">
					{tasksLoading ? (
						<div className="flex items-center justify-center py-8 text-gray-400">
							<Loader2 className="w-5 h-5 animate-spin" />
						</div>
					) : templateTasks.length === 0 ? (
						<div className="py-6 text-center text-sm text-gray-500">No tasks in this template</div>
					) : (
						<Table>
							<TableHeader>
								<TableRow className="bg-gray-50 hover:bg-gray-50">
									<TableHead className="w-10 text-xs text-center">Seq</TableHead>
									<TableHead className="text-xs">Task Name</TableHead>
									<TableHead className="w-28 text-xs">Priority</TableHead>
									<TableHead className="w-24 text-xs">Effort</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{templateTasks.map((task, index) => {
									const priorityStyle = PRIORITY_STYLES[task.priority || ''] || PRIORITY_STYLES['P3 - Medium']
									return (
										<TableRow key={task._id} className="h-8">
											<TableCell className="text-xs text-gray-500 text-center py-1">
												{task.seq || index + 1}
											</TableCell>
											<TableCell className="py-1">
												<div className="text-sm text-gray-900">{task.taskName}</div>
												{task.description && (
													<div className="text-xs text-gray-500 truncate max-w-[250px]">
														{task.description}
													</div>
												)}
											</TableCell>
											<TableCell className="py-1">
												{task.priority && (
													<span
														className="inline-block text-xs px-1.5 py-0.5 rounded font-medium"
														style={{
															color: priorityStyle.text,
															backgroundColor: priorityStyle.bg,
															border: `1px solid ${priorityStyle.border}`,
														}}
													>
														{task.priority.split(' - ')[0]}
													</span>
												)}
											</TableCell>
											<TableCell className="py-1 text-xs text-gray-600">
												{task.effort ? task.effort.split(' - ')[0] : ''}
											</TableCell>
										</TableRow>
									)
								})}
							</TableBody>
						</Table>
					)}
				</div>

				<div className="text-xs text-gray-400 text-center">
					{templateTasks.length} task{templateTasks.length !== 1 ? 's' : ''} in this template
				</div>
			</div>
		)
	}

	// ─── Template List View ─────────────────────────────────────────────────────

	return (
		<div className="min-w-[30rem] max-w-[45rem] p-3">
			{taskTemplates.length === 0 ? (
				<div className="py-8 text-center text-sm text-gray-500">No task templates available</div>
			) : (
				<div className="border border-gray-200 rounded-lg overflow-hidden">
					<Table>
						<TableHeader>
							<TableRow className="bg-gray-50 hover:bg-gray-50">
								<TableHead className="text-xs">Template</TableHead>
								<TableHead className="text-xs">Description</TableHead>
								<TableHead className="w-20 text-xs text-center">Download</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{taskTemplates.map(template => (
								<TableRow key={template._id} className="h-9 hover:bg-gray-50">
									<TableCell
										className="py-1.5 cursor-pointer"
										onClick={() => handleTemplateClick(template)}
									>
										<div className="flex items-center gap-1.5">
											<span className="text-sm text-gray-900 hover:text-blue-600 transition-colors">
												{template.templateName}
											</span>
											<ChevronRight className="w-3 h-3 text-gray-400" />
										</div>
									</TableCell>
									<TableCell
										className="py-1.5 text-sm text-gray-500 cursor-pointer"
										onClick={() => handleTemplateClick(template)}
									>
										{template.description || '—'}
									</TableCell>
									<TableCell className="py-1.5 text-center">
										<button
											type="button"
											className="inline-flex items-center justify-center w-7 h-7 rounded-full hover:bg-blue-50 transition-colors text-blue-600 bg-transparent border-none cursor-pointer"
											onClick={() => handleDownload(template)}
											title="Download template"
										>
											<Download className="h-4 w-4" />
										</button>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			)}
		</div>
	)
}

export default TaskTemplatesSelectCN
